// src/utils/exportLavorazioni.js
//
// Export Excel delle lavorazioni, UGUALE su tutte le pagine che lo offrono
// (tabellone in Lavorazioni, Lavorazioni aperte). Le colonne e il loro
// ordine sono quelli del foglio della segreteria (file "chiamate", settembre
// 2026): le segretarie copiano le righe e le incollano nel loro foglio, quindi
// NON cambiare ordine, nomi o numero delle colonne senza aggiornare anche il
// loro file.
//
// Accorgimenti per il copia e incolla:
//   - le date sono date vere di Excel in formato gg/mm/aaaa (non testo)
//   - codici, seriali e inventari restano testo: "003779" non perde gli zeri
//   - l'importo del preventivo e' un numero
//   - a capo e tabulazioni dentro i campi diventano spazi, cosi' una cella
//     incollata non si spezza su piu' righe
import * as XLSX from 'xlsx';

export const LAVORAZIONI_EXPORT_COLUMNS = [
  { key: 'codiceInterno', label: 'Codice Interno', width: 13 },
  { key: 'codiceEsterno', label: 'Codice Esterno', width: 18 },
  { key: 'dataRicezione', label: 'Data ricezione', width: 12 },
  { key: 'assegnatoA', label: 'Assegnato a', width: 20 },
  { key: 'dataIntervento', label: 'Data intervento', width: 12 },
  { key: 'cliente', label: 'Cliente', width: 24 },
  { key: 'luogo', label: 'Luogo', width: 22 },
  { key: 'presidio', label: 'Presidio', width: 22 },
  { key: 'modello', label: 'Modello', width: 16 },
  { key: 'seriale', label: 'Seriale', width: 14 },
  { key: 'inventario', label: 'Inventario', width: 14 },
  { key: 'consuntivo', label: 'Consuntivo', width: 11 },
  { key: 'impPreventivo', label: 'Imp. Preventivo', width: 14 },
  { key: 'rifPreventivo', label: 'Rif. preventivo', width: 16 },
  { key: 'rifOrdine', label: 'Rif.ordine', width: 16 },
  { key: 'materiale', label: 'Materiale', width: 10 },
];

const text = (value) =>
  String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

// Data a mezzanotte locale: niente orari che in Excel spostano il giorno
const excelDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const userLabel = (u) => {
  if (!u || typeof u !== 'object') return '';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  return full || u.username || '';
};

const internalCode = (order) => {
  const base = order.internalCode || order.codiceInterno || '';
  if (!base) return '';
  const iter = typeof order.iteration === 'number' ? order.iteration : 0;
  return iter > 0 ? `${base}-${iter}` : base;
};

const lastEventOf = (order) => {
  const events = Array.isArray(order.events) ? order.events : [];
  return events.length ? events[events.length - 1] : null;
};

/** Una lavorazione (come arriva da GET /api/work/orders) -> valori per colonna */
export function lavorazioneExportValues(order = {}) {
  const lastEvent = lastEventOf(order);
  const billing = order.billing || {};
  const importo = billing.importoPreventivo;

  return {
    codiceInterno: text(internalCode(order)),
    codiceEsterno: text(order.externalCode),
    dataRicezione: excelDate(order.receivedAt || order.dateReceived || order.dataRicezione),
    assegnatoA: text(userLabel(lastEvent?.assignedTo) || userLabel(order.assignedTo)),
    dataIntervento: excelDate(
      lastEvent?.date ||
        (Array.isArray(order.dates) && order.dates[0]) ||
        order.dateIntervento ||
        order.dataIntervento
    ),
    cliente: text(order.companyName || order.cliente),
    luogo: text(order.place || order.luogo),
    presidio: text(order.equipmentPresidio || order.presidio),
    modello: text(order.equipmentModel || order.modello),
    seriale: text(order.equipmentSerial || order.seriale),
    inventario: text(order.equipmentInventory || order.inventario),
    consuntivo: text(order.preventivoStatus === 'A consuntivo' ? order.consuntivoStatus : ''),
    impPreventivo:
      importo === null || importo === undefined || importo === '' || Number.isNaN(Number(importo))
        ? ''
        : Number(importo),
    rifPreventivo: text(order.rifPreventivo || billing.numeroPreventivo),
    rifOrdine: text(order.rifOrdineCliente || billing.riferimentiOrdineCliente),
    materiale: text(order.materialeArrivato),
  };
}

/** Righe (intestazione compresa) pronte per aoa_to_sheet */
export function lavorazioniExportRows(orders = []) {
  const header = LAVORAZIONI_EXPORT_COLUMNS.map((c) => c.label);
  const body = orders.map((o) => {
    const values = lavorazioneExportValues(o);
    return LAVORAZIONI_EXPORT_COLUMNS.map((c) => values[c.key]);
  });
  return [header, ...body];
}

export function lavorazioniWorkbook(orders = [], sheetName = 'Lavorazioni') {
  const ws = XLSX.utils.aoa_to_sheet(lavorazioniExportRows(orders), {
    dateNF: 'dd/mm/yyyy',
  });
  ws['!cols'] = LAVORAZIONI_EXPORT_COLUMNS.map((c) => ({ wch: c.width }));

  // importo con due decimali, sempre letto come numero
  const impCol = LAVORAZIONI_EXPORT_COLUMNS.findIndex((c) => c.key === 'impPreventivo');
  for (let r = 1; r <= orders.length; r += 1) {
    const cell = ws[XLSX.utils.encode_cell({ r, c: impCol })];
    if (cell && cell.t === 'n') cell.z = '#,##0.00';
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  return wb;
}

/** Scarica il file .xlsx nel browser */
export function downloadLavorazioniXlsx(orders, filename = 'lavorazioni.xlsx', sheetName = 'Lavorazioni') {
  XLSX.writeFile(lavorazioniWorkbook(orders, sheetName), filename);
}
